import React from 'react';
import type { KPIData } from '../../../utils/topClientsUtils';
import { fmtUSD } from '../../../utils/topClientsUtils';

interface KpiStripProps {
  kpis: KPIData;
  topN: number;
  isFYQuarterMode: boolean;
  periodLabel: string;
}

interface TileProps {
  accentColor: string;
  label: string;
  value: string;
  sub?: React.ReactNode;
}

const Tile: React.FC<TileProps> = ({ accentColor, label, value, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden flex items-stretch">
    <div className={`w-1 flex-none ${accentColor}`} />
    <div className="px-4 py-3.5 flex-1 min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1">{label}</p>
      <p className="text-[17px] font-bold text-[#0D0D0D] tabular-nums leading-tight">{value}</p>
      {sub && <div className="mt-1.5">{sub}</div>}
    </div>
  </div>
);

const RiskBadge: React.FC<{ level: 'High' | 'Medium' | 'Low' }> = ({ level }) => {
  const cls =
    level === 'High'
      ? 'bg-red-50 text-red-600 border-red-100'
      : level === 'Medium'
      ? 'bg-amber-50 text-amber-700 border-amber-100'
      : 'bg-emerald-50 text-emerald-700 border-emerald-100';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cls}`}>
      {level} Risk
    </span>
  );
};

export const KpiStrip: React.FC<KpiStripProps> = ({ kpis, topN, isFYQuarterMode, periodLabel }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
    {/* Portfolio Revenue */}
    <Tile
      accentColor="bg-blue-500"
      label={isFYQuarterMode ? `${periodLabel} Revenue` : 'Portfolio Revenue'}
      value={fmtUSD(kpis.totalRevenue)}
      sub={
        <p className="text-[11px] text-slate-600">
          {kpis.clientCount} {isFYQuarterMode ? 'clients in period' : 'total clients'}
        </p>
      }
    />

    {/* Top N Concentration */}
    <Tile
      accentColor="bg-emerald-500"
      label={`Top ${topN} Concentration`}
      value={`${kpis.topNPercentage.toFixed(1)}%`}
      sub={
        <div className="flex items-center gap-2 flex-wrap">
          <RiskBadge level={kpis.riskLevel} />
          <span className="text-[11px] text-slate-600">{fmtUSD(kpis.topNRevenue)}</span>
        </div>
      }
    />

    {/* Top 5 Concentration */}
    <Tile
      accentColor="bg-violet-500"
      label="Top 5 Concentration"
      value={`${kpis.top5Percentage.toFixed(1)}%`}
      sub={
        <p className="text-[11px] text-slate-600">
          {fmtUSD(kpis.top5Revenue)} elite revenue
        </p>
      }
    />

    {/* Portfolio Size */}
    <Tile
      accentColor="bg-amber-500"
      label="Portfolio Size"
      value={String(kpis.clientCount)}
      sub={
        <p className="text-[11px] text-slate-600">
          Avg {fmtUSD(kpis.avgRevenuePerClient)} / client
        </p>
      }
    />
  </div>
);

export default KpiStrip;
